import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BellAlertIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Card from '../ui/Card';
import StatusBanner from '../ui/StatusBanner';
import Button from '../ui/Button';
import Modal, { ConfirmModal } from '../ui/Modal';
import {
  adminNotificationsAPI,
  AdminNotification,
  AdminNotificationDetail,
  AdminNotificationHealth,
} from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';

const STATUSES = ['scheduled', 'pending', 'sending', 'sent', 'failed', 'abandoned', 'cancelled'];
const REQUEUEABLE = ['failed', 'abandoned', 'cancelled'];
const PAGE_SIZE = 25;

const statusBadge = (status: string): string => {
  switch (status) {
    case 'sent':
      return 'bg-clinic-primary/15 text-clinic-primary-dark';
    case 'pending':
    case 'scheduled':
    case 'sending':
      return 'bg-clinic-gold/20 text-clinic-brown';
    case 'failed':
    case 'abandoned':
      return 'bg-clinic-accent/15 text-clinic-accent';
    default: // cancelled
      return 'bg-clinic-muted/20 text-clinic-muted';
  }
};

const fmt = (s: string | null): string =>
  s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const AdminNotifications: React.FC = () => {
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [health, setHealth] = useState<AdminNotificationHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [recipient, setRecipient] = useState('');
  const [offset, setOffset] = useState(0);

  // Detail + requeue
  const [detail, setDetail] = useState<AdminNotificationDetail | null>(null);
  const [requeueTarget, setRequeueTarget] = useState<AdminNotification | null>(null);
  const [requeuing, setRequeuing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, h] = await Promise.all([
        adminNotificationsAPI.list({
          status: status || undefined,
          channel: channel || undefined,
          recipient: recipient || undefined,
          limit: PAGE_SIZE,
          offset,
        }),
        adminNotificationsAPI.health(),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setHealth(h);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  }, [status, channel, recipient, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (id: number) => {
    try {
      setDetail(await adminNotificationsAPI.getDetail(id));
    } catch {
      toast.error('Failed to load notification details');
    }
  };

  const doRequeue = async () => {
    if (!requeueTarget) return;
    try {
      setRequeuing(true);
      await adminNotificationsAPI.requeue(requeueTarget.id);
      toast.success('Notification requeued');
      setRequeueTarget(null);
      fetchData();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Requeue failed'));
    } finally {
      setRequeuing(false);
    }
  };

  const resetFilters = (setter: (v: string) => void) => (v: string) => { setOffset(0); setter(v); };
  const filters = [
    { label: 'Status', value: status, set: resetFilters(setStatus), opts: [['', 'All'], ...STATUSES.map((s) => [s, s] as [string, string])] },
    { label: 'Channel', value: channel, set: resetFilters(setChannel), opts: [['', 'All'], ['email', 'Email'], ['sms', 'SMS']] },
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold text-clinic-dark mb-8 font-body">Notifications</h1>

      {/* Health banner */}
      {health && !health.healthy && (
        <div className="mb-4">
          <StatusBanner
            variant="warning"
            title={`${health.stuck_count} notification${health.stuck_count === 1 ? '' : 's'} stuck`}
            message={`Pending or failed for more than ${health.threshold_minutes} minutes — the worker may be down or a send is failing.`}
          />
        </div>
      )}

      {/* Filter + status summary bar */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {filters.map((f) => (
            <div key={f.label}>
              <label className="block text-xs text-clinic-muted mb-1">{f.label}</label>
              <select
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                className="text-sm border border-clinic-primary-lighter rounded-input px-2 py-1.5 bg-white"
              >
                {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="block text-xs text-clinic-muted mb-1">Recipient</label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => { setOffset(0); setRecipient(e.target.value); }}
              placeholder="email or phone"
              className="text-sm border border-clinic-primary-lighter rounded-input px-2 py-1.5 bg-white"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} leftIcon={<ArrowPathIcon className="h-4 w-4" />}>
            Refresh
          </Button>
          {health && (
            <span className="text-xs text-clinic-muted ml-auto self-center">
              {Object.entries(health.by_status).map(([s, n]) => `${n} ${s}`).join(' · ') || 'no notifications'}
            </span>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary" />
          </div>
        ) : error ? (
          <div className="p-6">
            <StatusBanner variant="error" title="Unable to load notifications" message={error} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <BellAlertIcon className="h-12 w-12 text-clinic-muted mx-auto mb-3" />
            <p className="text-clinic-dark font-medium">No notifications found</p>
            <p className="text-sm text-clinic-muted mt-1">Try adjusting the filters above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-clinic-surface-sunken text-clinic-muted text-xs uppercase tracking-wide">
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Recipient</th>
                  <th className="text-left font-medium px-4 py-3">Template</th>
                  <th className="text-left font-medium px-4 py-3">Attempts</th>
                  <th className="text-left font-medium px-4 py-3">Created</th>
                  <th className="text-left font-medium px-4 py-3">Sent</th>
                  <th className="text-right font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id} className="border-t border-clinic-primary-lighter hover:bg-clinic-surface/60 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(n.status)}`}>
                        {n.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-clinic-dark">{n.recipient_email || n.recipient_phone || '—'}</td>
                    <td className="px-4 py-3 text-clinic-muted">{n.template_name}</td>
                    <td className="px-4 py-3 text-clinic-muted">{n.attempt_count}</td>
                    <td className="px-4 py-3 text-clinic-muted">{fmt(n.created_at)}</td>
                    <td className="px-4 py-3 text-clinic-muted">{fmt(n.sent_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(n.id)}>Details</Button>
                        {REQUEUEABLE.includes(n.status) && (
                          <Button variant="secondary" size="sm" onClick={() => setRequeueTarget(n)}>Requeue</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-clinic-primary-lighter">
            <span className="text-xs text-clinic-muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Previous
              </Button>
              <Button variant="ghost" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Requeue confirmation */}
      <ConfirmModal
        isOpen={requeueTarget !== null}
        onClose={() => { if (!requeuing) setRequeueTarget(null); }}
        onConfirm={doRequeue}
        title="Requeue notification?"
        message={`This resets the attempt counter and puts the ${requeueTarget?.template_name ?? ''} notification back on the queue to be sent again.`}
        confirmText="Requeue"
        isLoading={requeuing}
      />

      {/* Detail modal */}
      <Modal
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        title="Notification detail"
        size="lg"
      >
        {detail && (
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
              {([
                ['Status', detail.status],
                ['Channel', detail.channel],
                ['Template', detail.template_name],
                ['Recipient', detail.recipient_email || detail.recipient_phone || '—'],
                ['Subject', detail.subject || '—'],
                ['Attempts', String(detail.attempt_count)],
                ['Booking', detail.booking_id ? `#${detail.booking_id}` : '—'],
                ['Created', fmt(detail.created_at)],
                ['Sent', fmt(detail.sent_at)],
                ['Next retry', fmt(detail.next_retry_at)],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-clinic-muted uppercase tracking-wide">{k}</dt>
                  <dd className="text-clinic-dark">{v}</dd>
                </div>
              ))}
            </dl>
            {detail.error_message && (
              <div>
                <p className="text-xs text-clinic-muted uppercase tracking-wide mb-1">Last error</p>
                <p className="text-clinic-accent bg-clinic-accent/10 rounded-input px-3 py-2">{detail.error_message}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-clinic-muted uppercase tracking-wide mb-1">Render context</p>
              <pre className="text-xs text-clinic-dark bg-clinic-surface-sunken rounded-input p-3 overflow-x-auto">
                {JSON.stringify(detail.context_json, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminNotifications;
